import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { GripVertical, Trash2, Plus, Search } from 'lucide-react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export function PlaylistSongRow({ song, idx, playlistSongs, setPlaylistSongs, editingPlaylist, triggerPlaylistAutoSave, searchingSongIndex, showSongSearchResults, setShowSongSearchResults, setSearchingSongIndex, songSearchQuery, setSongSearchQuery, searchSongs, songSearchResults, searchingSongs, selectSongFromSearch, dragHandle, orderNum }) {
  const designationColors = { 'must_play': 'bg-green-500', 'play_if_possible': 'bg-amber-500', 'do_not_play': 'bg-red-500' };
  
  return (
    <div className="flex gap-2 items-start relative pl-2">
      {dragHandle}
      {orderNum && (
        <span className="text-white/30 text-xs font-mono w-6 pt-2.5 text-right flex-shrink-0">{orderNum}</span>
      )}
      {!orderNum && <div className={`w-1 rounded-full self-stretch ${designationColors[song.designation || 'must_play']}`}></div>}
      <div className="flex-1 space-y-1">
        <div className="flex gap-2 items-center">
          <Input
            value={song.title}
            onChange={(e) => {
              const updated = [...playlistSongs];
              updated[idx].title = e.target.value;
              setPlaylistSongs(updated);
              if (editingPlaylist) triggerPlaylistAutoSave();
            }}
            placeholder="Song title"
            className="bg-white/10 border-white/20 text-white text-sm"
            data-testid={`song-title-${idx}`}
          />
          <Input
            value={song.artist}
            onChange={(e) => {
              const updated = [...playlistSongs];
              updated[idx].artist = e.target.value;
              setPlaylistSongs(updated);
              if (editingPlaylist) triggerPlaylistAutoSave();
            }}
            placeholder="Artist"
            className="bg-white/10 border-white/20 text-white text-sm"
            data-testid={`song-artist-${idx}`}
          />
          <Select
            value={song.designation || 'must_play'}
            onValueChange={(val) => {
              const updated = [...playlistSongs];
              updated[idx].designation = val;
              setPlaylistSongs(updated);
              if (editingPlaylist) triggerPlaylistAutoSave();
            }}
          >
            <SelectTrigger className="bg-white/10 border-white/20 text-white w-32 text-xs" data-testid={`song-designation-${idx}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-white/20">
              <SelectItem value="must_play" className="text-white">Must Play</SelectItem>
              <SelectItem value="play_if_possible" className="text-white">If Possible</SelectItem>
              <SelectItem value="do_not_play" className="text-white">Do Not Play</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 flex-shrink-0"
            onClick={() => {
              const updated = playlistSongs.filter((_, i) => i !== idx);
              setPlaylistSongs(updated);
              if (editingPlaylist) triggerPlaylistAutoSave();
            }}
            data-testid={`song-remove-${idx}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Input
            value={song.notes || ''}
            onChange={(e) => {
              const updated = [...playlistSongs];
              updated[idx].notes = e.target.value;
              setPlaylistSongs(updated);
              if (editingPlaylist) triggerPlaylistAutoSave();
            }}
            placeholder="Notes (optional)"
            className="bg-white/5 border-white/10 text-white/70 text-xs"
            data-testid={`song-notes-${idx}`}
          />
          <div className="relative flex-shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="text-white/40 hover:text-white/70 hover:bg-white/10"
              onClick={() => {
                if (searchingSongIndex === idx) {
                  setShowSongSearchResults(false);
                  setSearchingSongIndex(null);
                } else {
                  setSearchingSongIndex(idx);
                  setSongSearchQuery(song.title || '');
                  if (song.title) searchSongs(song.title);
                }
              }}
              data-testid={`song-search-${idx}`}
            >
              <Search className="w-4 h-4" />
            </Button>
            {searchingSongIndex === idx && showSongSearchResults && (
              <div className="absolute right-0 top-full mt-1 w-80 bg-slate-800 border border-white/20 rounded-lg shadow-xl z-50 max-h-60 overflow-y-auto">
                <div className="p-2 border-b border-white/10">
                  <Input
                    value={songSearchQuery}
                    onChange={(e) => {
                      setSongSearchQuery(e.target.value);
                      searchSongs(e.target.value);
                    }}
                    placeholder="Search songs..."
                    className="bg-white/10 border-white/20 text-white text-xs"
                    autoFocus
                    data-testid="song-search-input"
                  />
                </div>
                {searchingSongs ? (
                  <div className="p-4 text-center text-white/50 text-sm">Searching...</div>
                ) : songSearchResults.length > 0 ? (
                  songSearchResults.map((result, i) => (
                    <button
                      key={i}
                      className="w-full text-left px-3 py-2 hover:bg-white/10 transition-colors"
                      onClick={() => selectSongFromSearch(result, idx)}
                      data-testid={`search-result-${i}`}
                    >
                      <p className="text-white text-sm">{result.title}</p>
                      <p className="text-white/50 text-xs">{result.artist}</p>
                    </button>
                  ))
                ) : songSearchQuery ? (
                  <div className="p-4 text-center text-white/50 text-sm">No results found</div>
                ) : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function SortablePlaylistSong({ id, song, idx, orderNum, ...props }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  
  return (
    <div ref={setNodeRef} style={style}>
      <PlaylistSongRow
        song={song}
        idx={idx}
        orderNum={orderNum}
        dragHandle={
          <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-1 pt-2.5 text-white/40 hover:text-white/70 flex-shrink-0">
            <GripVertical className="w-4 h-4" />
          </button>
        }
        {...props}
      />
    </div>
  );
}

export function SortableRepeaterRow({ id, row, idx, field, locked, handleRepeaterChange, removeRepeaterRow, rowCount, is3Col }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style} className="flex gap-2 items-start">
      {!locked && (
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing p-1 pt-2 text-white/40 hover:text-white/70 flex-shrink-0"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      )}
      {is3Col ? (
        <>
          <Input
            value={row?.col1 || ''}
            onChange={(e) => handleRepeaterChange(field.name, idx, 'col1', e.target.value, true)}
            placeholder={field.column1_label || 'Column 1'}
            disabled={locked}
            className={`bg-white/10 border-white/20 text-white flex-1 ${locked ? 'opacity-60' : ''}`}
          />
          <Input
            value={row?.col2 || ''}
            onChange={(e) => handleRepeaterChange(field.name, idx, 'col2', e.target.value, true)}
            placeholder={field.column2_label || 'Column 2'}
            disabled={locked}
            className={`bg-white/10 border-white/20 text-white flex-1 ${locked ? 'opacity-60' : ''}`}
          />
          <Input
            value={row?.col3 || ''}
            onChange={(e) => handleRepeaterChange(field.name, idx, 'col3', e.target.value, true)}
            placeholder={field.column3_label || 'Column 3'}
            disabled={locked}
            className={`bg-white/10 border-white/20 text-white flex-1 ${locked ? 'opacity-60' : ''}`}
          />
        </>
      ) : (
        <>
          <Input
            value={typeof row === 'object' ? (row?.col1 || '') : (row || '')}
            onChange={(e) => handleRepeaterChange(field.name, idx, 'col1', e.target.value)}
            placeholder={field.column1_label || 'Item'}
            disabled={locked}
            className={`bg-white/10 border-white/20 text-white flex-1 ${locked ? 'opacity-60' : ''}`}
          />
          <Input
            value={typeof row === 'object' ? (row?.col2 || '') : ''}
            onChange={(e) => handleRepeaterChange(field.name, idx, 'col2', e.target.value)}
            placeholder={field.column2_label || 'Details'}
            disabled={locked}
            className={`bg-white/10 border-white/20 text-white flex-1 ${locked ? 'opacity-60' : ''}`}
          />
        </>
      )}
      {!locked && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => removeRepeaterRow(field.name, idx)}
          disabled={rowCount <= 1}
          className="text-red-400 hover:text-red-300 flex-shrink-0"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}

export function SortableRepeater({ field, rows, locked, handleRepeaterChange, addRepeaterRow, removeRepeaterRow, reorderRepeaterRows, is3Col }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const rowIds = rows.map((_, i) => `repeater-${field.name}-${i}`);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = rowIds.indexOf(active.id);
      const newIndex = rowIds.indexOf(over.id);
      reorderRepeaterRows(field.name, oldIndex, newIndex);
    }
  };

  return (
    <div className="space-y-2">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
          {rows.map((row, i) => (
            <SortableRepeaterRow
              key={rowIds[i]}
              id={rowIds[i]}
              row={row}
              idx={i}
              field={field}
              locked={locked}
              handleRepeaterChange={handleRepeaterChange}
              removeRepeaterRow={removeRepeaterRow}
              rowCount={rows.length}
              is3Col={is3Col}
            />
          ))}
        </SortableContext>
      </DndContext>
      {!locked && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => addRepeaterRow(field.name, is3Col)}
          className="text-white/50 hover:text-white w-full border border-dashed border-white/20 hover:border-white/40"
        >
          <Plus className="w-4 h-4 mr-2" /> Add Row
        </Button>
      )}
    </div>
  );
}
